import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MemberProvider } from './contexts/MemberContext';
import SnackbarProvider from './contexts/SnackbarProvider';
import ProtectedRoute from './components/ProtectedRoute';
import MemberRoute from './components/MemberRoute';
import SetupPage from './pages/SetupPage';
import LandingPage from './pages/LandingPage';
import PhoneEntryPage from './pages/PhoneEntryPage';
import MarketingSignupPage from './pages/MarketingSignupPage';
import MemberDashboardPage from './pages/MemberDashboardPage';
import CashierLoginPage from './pages/CashierLoginPage';
import CashierCouponPage from './pages/CashierCouponPage';
import CashierTransactionPage from './pages/CashierTransactionPage';
import TransactionConfirmationPage from './pages/TransactionConfirmationPage';
import PointsEarnedPage from './pages/PointsEarnedPage';
import NotFoundPage from './pages/NotFoundPage';
import AnonymousRoute from './components/AnonymousRoute';
import ScaledPage from './components/ScaledPage';
import './App.css';
import MemberConfirmationPage from './pages/MemberConfirmationPage';
import AccountOptedOutPage from './pages/AccountOptedOutPage';
import AccountVerificationPendingPage from './pages/AccountVerificationPendingPage';
import useWakeLock from './hooks/useWakeLock';

// Default theme
const defaultTheme = {
  typography: {
    body1: {
      fontSize: '1.5rem',
    },
    body2: {
      fontSize: '1.2rem',
    },
    body3: {
      fontSize: '0.8rem',
    },
  },
  palette: {
    primary: {
      main: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc004e',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
  },
};

// AppContent component to access AuthContext
const AppContent = () => {
  const { deviceInfo } = useAuth();

  // Enable wake lock to prevent the tablet from sleeping
  useWakeLock();

  // Create a dynamic theme based on deviceInfo
  const theme = React.useMemo(() => {
    const customTheme = { ...defaultTheme };

    // Apply custom colors if available in deviceInfo
    if (deviceInfo) {
      if (deviceInfo.primary_color) {
        customTheme.palette.primary = {
          main: deviceInfo.primary_color,
          contrastText: '#ffffff',
        };
      }

      if (deviceInfo.secondary_color) {
        customTheme.palette.secondary = {
          main: deviceInfo.secondary_color,
          contrastText: '#ffffff',
        };
      }
    }

    return createTheme(customTheme);
  }, [deviceInfo]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route element={<AnonymousRoute />}>
            <Route path="/setup" element={<SetupPage />} />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/landing" element={<ScaledPage><LandingPage /></ScaledPage>} />
            <Route path="/phone-entry" element={<PhoneEntryPage />} />
            <Route path="/marketing-signup" element={<ScaledPage><MarketingSignupPage /></ScaledPage>} />

            {/* Member routes - require valid member data */}
            <Route element={<MemberRoute />}>
              <Route path="/member-dashboard" element={<ScaledPage><MemberDashboardPage /></ScaledPage>} />
              <Route path="/member-confirmation" element={<ScaledPage><MemberConfirmationPage /></ScaledPage>} />
              <Route path="/cashier-login" element={<CashierLoginPage />} />
              <Route path="/cashier-coupon" element={<ScaledPage><CashierCouponPage /></ScaledPage>} />
              <Route path="/cashier-transaction" element={<CashierTransactionPage />} />
              <Route path="/transaction-confirmation" element={<ScaledPage><TransactionConfirmationPage /></ScaledPage>} />
              <Route path="/points-earned" element={<ScaledPage><PointsEarnedPage /></ScaledPage>} />
              <Route path="/account-opted-out" element={<ScaledPage><AccountOptedOutPage /></ScaledPage>} />
              <Route path="/account-verification-pending" element={<ScaledPage><AccountVerificationPendingPage /></ScaledPage>} />
              {/* Add more member-specific routes here */}
            </Route>

            {/* Add more protected routes here */}
          </Route>

          {/* Root route - redirect to setup page by default */}
          <Route path="/" element={<Navigate to="/setup" replace />} />

          {/* Not found page */}
          <Route path="*" element={<ScaledPage><NotFoundPage /></ScaledPage>} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <MemberProvider>
        <SnackbarProvider>
          <AppContent />
        </SnackbarProvider>
      </MemberProvider>
    </AuthProvider>
  );
}

export default App;